import React from "react";
import styled from "styled-components";
import {
  Stack,
  Heading,
  Text,
  Alert,
  Accordion,
  RadioGroup,
  Radio,
  Checkbox,
  TextField,
  SelectField,
  TextLink,
  Inline,
  Box,
} from "@origin-digital/ods-core";
import { IconCheck } from "@origin-digital/ods-icons";
import { useField, useFormikContext } from "formik";
import { State } from "src/components/Premises/types";
import ResponsiveColumn from "./ResponsiveColumn";
import ReadOnlyTextField from "../FormComponents/ReadOnlyTextField";
import { getConcessionDisclaimer, RebateInfo } from "./concessionDisclaimer";
import {
  getConcessionCardLabel,
  getConcessionCardOptions,
} from "./concessionCardTypes";
import {
  getConcessionConsentScript,
  shouldShowServicesAustraliaConsent,
} from "./concessionConsentScripts";
import ConsentScriptContent from "./ConsentScriptContent";
import { renderWithBold } from "./renderWithBold";
import { ConcessionStatus } from "./types";

// transparent accordion override so the consent block visually merges with the wrapping Alert.
const TransparentAccordion = styled.div`
  & .MuiAccordion-root {
    background-color: transparent;
    background-image: none;
    box-shadow: none;
    border: none;
    margin: 0;
  }
  & .MuiAccordion-root::before {
    display: none;
  }
  & .MuiAccordion-root.Mui-expanded {
    margin: 0;
  }
  & .MuiAccordionSummary-root {
    background-color: transparent;
    padding: 0;
    min-height: 0;
  }
  & .MuiAccordionSummary-root.Mui-expanded {
    min-height: 0;
  }
  & .MuiAccordionSummary-content {
    margin: 8px 0;
  }
  & .MuiAccordionSummary-content.Mui-expanded {
    margin: 8px 0;
  }
  & .MuiAccordionDetails-root {
    background-color: transparent;
    padding: 8px 0 0 0;
  }
`;

const RebateList: React.FC<{ items: string[] }> = ({ items }) => (
  <Stack space="small">
    {items.map((item, idx) => (
      <Inline space="xsmall" alignY="top" key={idx}>
        <Box paddingTop="xxsmall">
          <IconCheck size="xxsmall" />
        </Box>
        <Text weight="medium">{item}</Text>
      </Inline>
    ))}
  </Stack>
);

const RebateInfoContent: React.FC<{ info: RebateInfo }> = ({ info }) => (
  <Stack space="medium">
    <Text>{renderWithBold(info.intro, info.introBoldPhrases)}</Text>
    {info.primaryRebates && info.primaryRebates.length > 0 && (
      <RebateList items={info.primaryRebates} />
    )}
    {info.secondaryIntro && (
      <Text>
        {renderWithBold(info.secondaryIntro, info.secondaryIntroBoldPhrases)}
      </Text>
    )}
    <Text>
      {info.closingBeforeLink}
      <TextLink
        href={info.linkUrl}
        target="_blank"
        domProps={{ rel: "noopener noreferrer" }}
      >
        {info.linkLabel}
      </TextLink>
      {info.closingAfterLink ?? ""}
    </Text>
    {info.secondaryRebates && info.secondaryRebates.length > 0 && (
      <RebateList items={info.secondaryRebates} />
    )}
  </Stack>
);

interface ConcessionBlockProps {
  state: State | "";
  readOnly?: boolean;
}

const CONCESSION_NUMBER_REGEX = /[^A-Za-z0-9]/g;

const CONCESSION_DEPENDENT_FIELDS: {
  name: string;
  initial: string | boolean;
}[] = [
  { name: "concessionCardType", initial: "" },
  { name: "concessionNumber", initial: "" },
  { name: "consentElectronicSignature", initial: false },
  { name: "consentLegallyBinding", initial: false },
  { name: "consentElectronicSignatureServicesAustralia", initial: false },
  { name: "consentLegallyBindingServicesAustralia", initial: false },
];

const ConcessionBlock: React.FC<ConcessionBlockProps> = ({
  state,
  readOnly = false,
}) => {
  const [statusField, statusMeta, statusHelpers] =
    useField<ConcessionStatus>("concessionStatus");
  const [cardTypeField, cardTypeMeta, cardTypeHelpers] =
    useField<string>("concessionCardType");
  const [numberField, numberMeta, numberHelpers] =
    useField<string>("concessionNumber");
  const [esField, esMeta, esHelpers] = useField<boolean>(
    "consentElectronicSignature"
  );
  const [lbField, lbMeta, lbHelpers] = useField<boolean>(
    "consentLegallyBinding"
  );
  const [esSaField, esSaMeta, esSaHelpers] = useField<boolean>(
    "consentElectronicSignatureServicesAustralia"
  );
  const [lbSaField, lbSaMeta, lbSaHelpers] = useField<boolean>(
    "consentLegallyBindingServicesAustralia"
  );
  const { setFieldValue, setFieldTouched } = useFormikContext();

  const disclaimer = getConcessionDisclaimer(state);
  const isYes = statusField.value === true;
  // SA does not collect a card number or require consent
  const isSA = state === State.SA;
  const cardOptions = getConcessionCardOptions(state);
  const showCardType = isYes && cardOptions.length > 0;

  const showSaConsent =
    isYes && shouldShowServicesAustraliaConsent(state, cardTypeField.value);

  //reset card type if no longer yes, or if options change and current value is invalid
  React.useEffect(() => {
    if (!cardTypeField.value) return;
    const stillValid = cardOptions.some(
      (opt) => opt.value === cardTypeField.value
    );
    if (!stillValid) {
      cardTypeHelpers.setValue("");
      cardTypeHelpers.setTouched(false, false);
    }
  }, [state, cardTypeField.value]);

  // Clear SA consents when the set is hidden so a ticked box does not persist.
  React.useEffect(() => {
    if (showSaConsent) return;
    if (esSaField.value) {
      esSaHelpers.setValue(false);
      esSaHelpers.setTouched(false, false);
    }
    if (lbSaField.value) {
      lbSaHelpers.setValue(false);
      lbSaHelpers.setTouched(false, false);
    }
  }, [showSaConsent]);

  const handleStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value === "true";
    statusHelpers.setValue(next);
    statusHelpers.setTouched(true, false);
    if (!next) {
      // Reset dependents so stale values do not leak into payload.
      CONCESSION_DEPENDENT_FIELDS.forEach(({ name, initial }) => {
        setFieldValue(name, initial, false);
        setFieldTouched(name, false, false);
      });
    }
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const stripped = e.target.value.replace(CONCESSION_NUMBER_REGEX, "");
    numberHelpers.setValue(stripped);
  };

  if (readOnly) {
    return (
      <Stack space={["large", "xlarge"]}>
        <Heading variant="h4">Concession</Heading>
        <ReadOnlyTextField
          label="Do you have a concession card?"
          value={statusField.value === true ? "Yes" : "No"}
        />
        {statusField.value === true && (
          <>
            {cardTypeField.value && (
              <ReadOnlyTextField
                label="Concession card type"
                value={getConcessionCardLabel(cardTypeField.value)}
              />
            )}
            {!isSA && (
              <>
                <ReadOnlyTextField
                  label="Concession card number"
                  value={numberField.value}
                />
                <ReadOnlyTextField
                  label="Electronic signature consent"
                  value={esField.value ? "Accepted" : "Not accepted"}
                />
                <ReadOnlyTextField
                  label="Legally binding consent"
                  value={lbField.value ? "Accepted" : "Not accepted"}
                />
              </>
            )}
          </>
        )}
      </Stack>
    );
  }

  return (
    <Stack space={["large", "xlarge"]}>
      <Heading variant="h4">Concession</Heading>

      {disclaimer && (
        <Alert title={disclaimer.heading} id="concession-disclaimer">
          <Stack space="medium">
            <Text>{disclaimer.body}</Text>
            <Text>{disclaimer.eligibilityNote}</Text>
            {disclaimer.eligibilityLink && (
              <Text>
                {disclaimer.eligibilityLink.textBeforeLink}
                <TextLink
                  href={disclaimer.eligibilityLink.linkUrl}
                  target="_blank"
                >
                  {disclaimer.eligibilityLink.linkLabel}
                </TextLink>
                {disclaimer.eligibilityLink.textAfterLink}
              </Text>
            )}
          </Stack>
        </Alert>
      )}

      <RadioGroup
        label="Do you have a concession card?"
        name="concessionStatus"
        value={
          statusField.value === null ? "" : statusField.value ? "true" : "false"
        }
        onChange={handleStatusChange}
        error={Boolean(statusMeta.touched && statusMeta.error)}
        helperText={(statusMeta.touched && statusMeta.error) || ""}
      >
        <Radio id="concession-yes" label="Yes" value="true" />
        <Radio id="concession-no" label="No" value="false" />
      </RadioGroup>

      {isYes && (
        <Stack space={["xlarge", "xlarge"]}>
          {/* conditionally show card type dropdown if there are options for the state. SA has no options and thus no dropdown. */}
          <Stack space="medium">
            {showCardType && (
              <ResponsiveColumn>
                <SelectField
                  id="concessionCardType"
                  name="concessionCardType"
                  variant="outlined"
                  label="Concession card type"
                  displayEmpty
                  emptyAriaLabel="Select a concession card type"
                  options={cardOptions}
                  value={cardTypeField.value}
                  onChange={(e) => {
                    cardTypeHelpers.setValue(e.target.value);
                    cardTypeHelpers.setTouched(true, false);
                  }}
                  onBlur={cardTypeField.onBlur}
                  error={Boolean(cardTypeMeta.touched && cardTypeMeta.error)}
                  helperText={
                    (cardTypeMeta.touched && cardTypeMeta.error) || ""
                  }
                />
              </ResponsiveColumn>
            )}
            {!isSA && (
              <ResponsiveColumn>
                <TextField
                  id="concessionNumber"
                  variant="outlined"
                  label="Concession card number"
                  value={numberField.value}
                  onChange={handleNumberChange}
                  onBlur={numberField.onBlur}
                  error={Boolean(numberMeta.touched && numberMeta.error)}
                  helperText={(numberMeta.touched && numberMeta.error) || ""}
                />
              </ResponsiveColumn>
            )}
          </Stack>
          {disclaimer?.rebateInfo && (
            <Stack space="small">
              <Heading variant="h4">Important Information</Heading>
              <Alert
                title={disclaimer.rebateInfo.heading}
                id="concession-rebate-info"
              >
                <RebateInfoContent info={disclaimer.rebateInfo} />
              </Alert>
            </Stack>
          )}
          {(() => {
            const script = getConcessionConsentScript(
              state,
              cardTypeField.value
            );
            if (!script) return null;
            return (
              <Alert title={script.heading} id="concession-consent-script">
                <TransparentAccordion>
                  <Accordion
                    id="concession-consent-script-accordion"
                    summary={
                      <Text weight="medium">View full consent terms</Text>
                    }
                    defaultExpanded
                    size="small"
                  >
                    <ConsentScriptContent script={script} />
                  </Accordion>
                </TransparentAccordion>
              </Alert>
            );
          })()}
          {!isSA && (
            <Stack space="medium">
              {showSaConsent && (
                <Text weight="medium">State based authority</Text>
              )}
              <Stack space="small">
                <Checkbox
                  id="consentElectronicSignature"
                  label="I confirm that the electronic signature in this consent represents my signature."
                  checked={esField.value}
                  onChange={(e) => {
                    esHelpers.setValue(e.target.checked);
                    esHelpers.setTouched(true, false);
                  }}
                />
                {esMeta.touched && esMeta.error && (
                  <Text variant="body-small" tone="critical">
                    {esMeta.error}
                  </Text>
                )}
              </Stack>
              <Stack space="small">
                <Checkbox
                  id="consentLegallyBinding"
                  label="I consent to signing this form electronically and I confirm that my signature is legally binding."
                  checked={lbField.value}
                  onChange={(e) => {
                    lbHelpers.setValue(e.target.checked);
                    lbHelpers.setTouched(true, false);
                  }}
                />
                {lbMeta.touched && lbMeta.error && (
                  <Text variant="body-small" tone="critical">
                    {lbMeta.error}
                  </Text>
                )}
              </Stack>
            </Stack>
          )}
          {/* Services Australia consent shown based on state and card type */}
          {showSaConsent && (
            <Stack space="medium">
              <Text weight="medium">Services Australia</Text>
              <Stack space="small">
                <Checkbox
                  id="consentElectronicSignatureServicesAustralia"
                  label="I confirm that the electronic signature in this consent represents my signature."
                  checked={esSaField.value}
                  onChange={(e) => {
                    esSaHelpers.setValue(e.target.checked);
                    esSaHelpers.setTouched(true, false);
                  }}
                />
                {esSaMeta.touched && esSaMeta.error && (
                  <Text variant="body-small" tone="critical">
                    {esSaMeta.error}
                  </Text>
                )}
              </Stack>
              <Stack space="small">
                <Checkbox
                  id="consentLegallyBindingServicesAustralia"
                  label="I consent to signing this form electronically and I confirm that my signature is legally binding."
                  checked={lbSaField.value}
                  onChange={(e) => {
                    lbSaHelpers.setValue(e.target.checked);
                    lbSaHelpers.setTouched(true, false);
                  }}
                />
                {lbSaMeta.touched && lbSaMeta.error && (
                  <Text variant="body-small" tone="critical">
                    {lbSaMeta.error}
                  </Text>
                )}
              </Stack>
            </Stack>
          )}
        </Stack>
      )}

      {disclaimer?.footerNote && (
        <Text variant="body-small" tone="neutralLight">
          {disclaimer.footerNote}
        </Text>
      )}
    </Stack>
  );
};

export default ConcessionBlock;
